import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import Layout from '@/components/Layout';
import axios from 'axios';
import { Users, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

import { BACKEND_URL, API } from '../config';
const o = { withCredentials: true };

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [bots, setBots] = useState([]);
  const [news, setNews] = useState([]);
  const [equity, setEquity] = useState([]);
  const [refStats, setRefStats] = useState(null);
  const [walletInput, setWalletInput] = useState('');
  const [paperBal, setPaperBal] = useState(null);
  const [walletReal, setWalletReal] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      // Curva de equity: TODA la actividad del usuario, sin límite de 200
      // (esa ventana hacía que la curva bajara aunque el P&L real siguiera
      // subiendo) y arrancando desde el ajuste de reseteo de saldo paper
      // vigente, para que no siga mostrando pérdidas ya reseteadas.
      axios.get(`${API}/portfolio/trades-full`, o).catch(() => ({ data: { trades: [], ajuste_paper: 0 } })).then(r => {
        const rows = r.data.trades || [];
        let acc = r.data.ajuste_paper || 0;
        setEquity(rows.map((t, i) => ({ i, equity: Number((acc += t.pnl).toFixed(2)), fecha: (t.ts || '').slice(5, 10) })));
      });
      axios.get(`${API}/referrals/stats`, o).then(r => {
        setRefStats(r.data);
        setWalletInput(r.data.wallet_bsc || '');
      }).catch(() => {});
      axios.get(`${API}/paper/balance`, o).then(r => setPaperBal(r.data)).catch(() => {});
      axios.get(`${API}/wallet`, o).then(r => setWalletReal(r.data)).catch(() => {});
      // Noticias en paralelo — no bloquean el dashboard (API externa puede tardar)
      axios.get(`${API}/market/news?limit=5`, o).then(r => setNews(r.data.news || [])).catch(() => {});
      const [statsRes, botsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, o),
        axios.get(`${API}/bots`, o),
      ]);
      setStats(statsRes.data);
      setBots(botsRes.data.bots || []);
    } catch (error) {
      toast.error('Error al cargar dashboard');
    } finally {
      setLoading(false);
    }
  };

  const saveWallet = async () => {
    if (!walletInput.trim()) return;
    try {
      await axios.post(`${API}/user/wallet-address?address=${encodeURIComponent(walletInput.trim())}`, {}, o);
      toast.success('Wallet BNB Smart Chain guardada');
    } catch { toast.error('Error al guardar wallet'); }
  };

  if (loading) {
    return (<Layout><div className="flex items-center justify-center h-full"><div className="text-center"><div className="aegis-spinner mx-auto"></div><p className="text-[#A1A1AA] text-sm mt-3">Cargando...</p></div></div></Layout>);
  }

  const tree = refStats?.tree || [];
  const flatList = [];
  const flatten = (nodes, level = 1) => {
    for (const n of nodes) {
      flatList.push({ ...n, level });
      if (n.referrals?.length) flatten(n.referrals, level + 1);
    }
  };
  flatten(tree);

  return (
    <Layout>
      <div className="space-y-6" data-testid="dashboard-page">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">Dashboard</h1>
          <p className="text-[#A1A1AA] mt-1">Resumen de tu actividad de trading</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-[#121212] border border-[#27272A] rounded-sm p-5">
            <p className="text-xs text-[#A1A1AA] uppercase tracking-wider">Ganancia Total</p>
            <p className={`text-2xl font-bold mt-1 mono ${stats?.total_profit_usd >= 0 ? 'text-positive' : 'text-negative'}`}>${stats?.total_profit_usd?.toFixed(2) || '0.00'}</p>
          </div>
          <div className="bg-[#121212] border border-[#27272A] rounded-sm p-5">
            <p className="text-xs text-[#A1A1AA] uppercase tracking-wider">Bots Activos</p>
            <p className="text-2xl font-bold text-white mt-1 mono">{stats?.active_bots || 0}/{stats?.total_bots || 0}</p>
          </div>
          <div className="bg-[#121212] border border-[#27272A] rounded-sm p-5">
            <p className="text-xs text-[#A1A1AA] uppercase tracking-wider">Comisión por Referidos</p>
            <p className="text-2xl font-bold text-[#00D4FF] mt-1 mono">${refStats?.total_commission_usd?.toFixed(2) || '0.00'}</p>
          </div>
          <div className="bg-[#121212] border border-[#27272A] rounded-sm p-5">
            <p className="text-xs text-[#A1A1AA] uppercase tracking-wider">Total Referidos</p>
            <p className="text-2xl font-bold text-white mt-1 mono">{refStats?.total_referrals || 0}</p>
          </div>
        </div>

        {/* Equity Chart */}
        {equity.length >= 1 && (
          <div className="bg-[#121212] border border-[#27272A] rounded-sm p-5">
            <h2 className="text-lg font-bold text-white mb-3">Curva de Equity</h2>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={equity}>
                <XAxis dataKey="fecha" tick={{ fill: '#A1A1AA', fontSize: 11 }} />
                <YAxis tick={{ fill: '#A1A1AA', fontSize: 11 }} />
                <Tooltip contentStyle={{ backgroundColor: '#1E1E1E', border: '1px solid #27272A', borderRadius: 4, color: '#fff' }} />
                <Line type="monotone" dataKey="equity" stroke="#00D4FF" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Saldos: Paper + Inversión real */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-[#121212] border border-[#27272A] rounded-sm p-5">
            <p className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-1">Saldo Paper Trading (virtual)</p>
            <p className="text-3xl font-bold text-[#00D4FF] mono">${paperBal?.saldo_actual?.toFixed(2) || '10,000.00'}</p>
            <div className="flex gap-4 mt-2 text-xs text-[#A1A1AA]">
              <span>Inicial: $10,000.00</span>
              <span className={`${(paperBal?.pnl_acumulado || 0) >= 0 ? 'text-positive' : 'text-negative'}`}>P&L: ${paperBal?.pnl_acumulado?.toFixed(2) || '0.00'}</span>
            </div>
            <p className="text-xs text-[#52525B] mt-1">{paperBal?.moneda || 'USDT (virtual)'} — para practicar sin riesgo</p>
          </div>

          <div className="bg-[#121212] border border-[#27272A] rounded-sm p-5">
            <p className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-1">Inversión Real (monedero)</p>
            <p className="text-3xl font-bold text-[#00FF88] mono">${(walletReal?.balance ?? 0).toFixed(2)}</p>
            <div className="flex gap-4 mt-2 text-xs text-[#A1A1AA]">
              <span>Cuotas pagadas (20%): ${(walletReal?.total_fees_paid ?? 0).toFixed(2)}</span>
              <span>Ganado por referidos: ${(walletReal?.total_referral_earned ?? 0).toFixed(2)}</span>
            </div>
            <p className="text-xs text-[#52525B] mt-1">Saldo real para operar — si llega a $0 se pausan las operaciones reales</p>
          </div>
        </div>

        {/* Monedero BSC + Referidos */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Monedero BNB Smart Chain */}
          <div className="bg-[#121212] border border-[#27272A] rounded-sm p-5">
            <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2"><Wallet size={20} className="text-[#FFD600]" /> Mi Monedero BNB Smart Chain</h2>
            <div className="space-y-3">
              <div className="flex items-center gap-2 p-3 bg-[#1E1E1E] rounded border border-[#27272A]">
                <input value={walletInput} onChange={e => setWalletInput(e.target.value)}
                  placeholder="Tu wallet BNB Smart Chain (0x...)"
                  className="bg-transparent border-none text-white text-sm flex-1 outline-none placeholder-[#52525B]" />
                <button onClick={saveWallet} className="px-3 py-1.5 rounded bg-[#FFD600] text-black text-xs font-bold whitespace-nowrap">Guardar</button>
              </div>
              {walletInput && (
                <div className="p-3 bg-[#1E1E1E] rounded border border-[#27272A]">
                  <p className="text-xs text-[#A1A1AA] mb-1">Wallet registrada:</p>
                  <p className="text-[#FFD600] mono text-sm break-all">{walletInput}</p>
                </div>
              )}
              <p className="text-xs text-[#52525B]">Esta dirección se usa para recibir los pagos de comisiones por referidos. Asegúrate de que sea BNB Smart Chain.</p>
            </div>
          </div>

          {/* Referidos */}
          <div className="bg-[#121212] border border-[#27272A] rounded-sm p-5">
            <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2"><Users size={20} className="text-[#00D4FF]" /> Mis Referidos</h2>
            {flatList.length > 0 ? (
              <div className="border border-[#27272A] rounded overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#1E1E1E] text-[#A1A1AA] text-xs">
                      <th className="text-left p-2">Nombre</th>
                      <th className="text-left p-2">Nivel</th>
                      <th className="text-right p-2">Sus Ganancias</th>
                      <th className="text-right p-2">Tu 5%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {flatList.map((r, i) => (
                      <tr key={i} className="border-t border-[#27272A]/50 hover:bg-[#1E1E1E]">
                        <td className="p-2 text-white" style={{ paddingLeft: 8 + (r.level - 1) * 16 }}>
                          {r.level > 1 && <span className="text-[#52525B] mr-1">↳</span>}{r.name}
                        </td>
                        <td className="p-2 text-[#A1A1AA]">{r.level}</td>
                        <td className={`p-2 text-right mono ${r.profits_usd > 0 ? 'text-positive' : 'text-[#A1A1AA]'}`}>${r.profits_usd.toFixed(2)}</td>
                        <td className="p-2 text-right mono text-[#00D4FF] font-bold">${r.your_commission_usd.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-[#A1A1AA] text-sm py-4 text-center">Aún no tienes referidos.</p>
            )}
            {refStats?.total_commission_usd > 0 && (
              <div className="mt-3 flex items-center gap-3 text-sm p-3 bg-[#00D4FF]/5 rounded border border-[#00D4FF]/20">
                <span className="text-[#A1A1AA]">Total a recibir:</span>
                <span className="text-[#00D4FF] font-bold text-lg mono">${refStats.total_commission_usd.toFixed(2)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Bots & News */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-[#121212] border border-[#27272A] rounded-sm p-5">
            <h2 className="text-lg font-bold text-white mb-3">Bots Recientes</h2>
            {bots.length === 0 ? (
              <p className="text-[#A1A1AA] text-center py-6">Aún no has creado bots</p>
            ) : (
              <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                {bots.map(bot => (
                  <div key={bot.bot_id} className="flex items-center justify-between p-3 bg-[#1E1E1E] rounded border border-[#27272A]">
                    <div>
                      <p className="text-white font-medium">{bot.name}</p>
                      <p className="text-xs text-[#A1A1AA] mono">{bot.symbol}</p>
                    </div>
                    <div className="text-right">
                      <span className={`inline-block px-2 py-1 rounded text-xs uppercase ${bot.status === 'running' ? 'bg-[#00FF88]/10 text-[#00FF88]' : 'bg-[#52525B]/10 text-[#52525B]'}`}>{bot.status}</span>
                      <p className={`text-sm mono mt-1 ${(bot.total_profit_usd || 0) >= 0 ? 'text-positive' : 'text-negative'}`}>${(bot.total_profit_usd || 0).toFixed(2)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="bg-[#121212] border border-[#27272A] rounded-sm p-5">
            <h2 className="text-lg font-bold text-white mb-3">Últimas Noticias</h2>
            {news.length === 0 ? (
              <p className="text-[#A1A1AA] text-center py-6">Sin noticias disponibles</p>
            ) : (
              <div className="space-y-2">
                {news.map((item, idx) => (
                  <a key={idx} href={item.url} target="_blank" rel="noopener noreferrer"
                    className="block p-3 bg-[#1E1E1E] rounded border border-[#27272A] hover:border-[#00D4FF] transition-colors">
                    <p className="text-white text-sm line-clamp-2">{item.title}</p>
                    <p className="text-xs text-[#A1A1AA] mt-1">{item.source}</p>
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
